import {Module} from '@fantasmagames/slot/src/common/Module';
import {Views} from '@fantasmagames/slot/src/common/view/Views';
import {LogicEvents} from '@fantasmagames/slot/src/logic/LogicEvents';
import {AutoplayHandlerEvents} from '@fantasmagames/slot/src/modules/autoplay/AutoplayHandlerEvents';
import {BonusGameEvents} from '@fantasmagames/slot/src/modules/bonus/BonusGameEvents';
import {FlowModuleEvents} from '@fantasmagames/slot/src/modules/flow/FlowModuleEvents';
import {FreeSpinsEvents} from '@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsEvents';
import {SpinButtonEvents} from '@fantasmagames/slot/src/modules/keypad/spinbutton/SpinButtonEvents';
import {SpinButtonView} from '@fantasmagames/slot/src/modules/keypad/spinbutton/SpinButtonView';
import {SpinButtonViewEvents} from '@fantasmagames/slot/src/modules/keypad/spinbutton/SpinButtonViewEvents';
import {SpinButtonBasicState} from '@fantasmagames/slot/src/modules/keypad/spinbutton/states/SpinButtonBasicState';
import {SpinButtonFreeSpinsIntoState} from '@fantasmagames/slot/src/modules/keypad/spinbutton/states/SpinButtonFreeSpinsIntoState';
import {SpinButtonFreeSpinsState} from '@fantasmagames/slot/src/modules/keypad/spinbutton/states/SpinButtonFreeSpinsState';
import {SpinButtonWhileSpinningState} from '@fantasmagames/slot/src/modules/keypad/spinbutton/states/SpinButtonWhileSpinningState';
import {StopAutoplayButtonEvents} from '@fantasmagames/slot/src/modules/keypad/stopautoplaybutton/StopAutoplayButtonEvents';
import {MainMenuEvents} from '@fantasmagames/slot/src/modules/mainmenu/MainMenuEvents';
import {SettingsEvents} from '@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsEvents';
import {AutoplayMenuEvents} from '@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/AutoplayMenuEvents';
import {BetlevelMenuEvents} from '@fantasmagames/slot/src/modules/popupmenus/betmenu/BetlevelMenuEvents';
import {InfoPopupEvents} from '@fantasmagames/slot/src/modules/popupmenus/info/InfoPopupEvents';
import {ReelsEvents} from '@fantasmagames/slot/src/modules/reels/spin/ReelsEvents';
import {StartScreenEvents} from '@fantasmagames/slot/src/modules/startscreen/StartScreenEvents';
import {SwipeInteractionEvents} from '@fantasmagames/slot/src/modules/swipeinteraction/SwipeInteractionEvents';
import {Timer} from '@fantasmagames/core/src/update/Timer';
import {InfoPopupTypes} from '@fantasmagames/slot/src/modules/popupmenus/info/InfoPopupTypes';

export class SpinButtonModule extends Module<SpinButtonView> {
	public static readonly BASIC_STATE: string = 'basic';
	public static readonly REELS_SPINNING_STATE: string = 'spinning';
	public static readonly FREE_SPINS_STATE: string = 'freespins';
	public static readonly FREE_SPINS_INTRO_STATE: string = 'freespinsintro';

	protected _spaceRunsSpin: boolean = true;
	protected _canSpin: boolean = true;
	protected _canSwipeSpin: boolean = true;
	protected _isInAutoplay: boolean = false;

	protected _hasQuickstop: boolean = false;

	protected _inputFunction: any;

	protected _isKeyboardInputEnabled: boolean = true;
	protected _isInFreeSpins: boolean = false;

	protected _autoplayMenuOpen: boolean = false;
	protected _infoPopupOpen: boolean = false;

	protected _hasSpinResponse: boolean = false;

	protected _regulationSpinTimer: Timer;
	protected _spinTimeRegulation: number = 0;
	protected _regulationCanSpin: boolean = true;

	protected _canSpinBySpinButton: boolean = true;

	public afterInit(): void {
		super.afterInit();

		this.view.hideSpinButton();

		this._hasQuickstop = true;
	}

	public init(): void {
		super.init();

		this.setupCertificationSettings();
	}

	public hasSpinTimeRegulation(): boolean {
		return this._spinTimeRegulation !== 0;
	}

	public startRegulationSpinTimer(): void {
		this.eventManager.emit(SpinButtonEvents.REGULATION_SPIN_DELAY_STARTED);
		this._regulationSpinTimer = new Timer(
			() => {
				this._regulationCanSpin = true;
				this.eventManager.emit(SpinButtonEvents.REGULATION_SPIN_DELAY_PASSED);
			},
			this._spinTimeRegulation * 1.1,
			true,
		);
		this.updateManager.add(this._regulationSpinTimer);
	}

	public setSpinStarted(): void {
		this.canSpin = false;
	}

	protected setupCertificationSettings(): void {
		this._spinTimeRegulation = 0;
	}

	public validateSpaceSpinPress(): boolean {
		return this._spaceRunsSpin;
	}

	public validateSpinPress(): boolean {
		return this.canSpin && !this._isInAutoplay && !this._isInFreeSpins && this.view.isVisible();
	}

	public validateQuickStop(): boolean {
		return this.hasQuickstop && !this.canSpin && this._hasSpinResponse;
	}

	public validateAutoplaySpinPress(): boolean {
		return this.canSpin && this._isInAutoplay && !this._isInFreeSpins;
	}

	public userHasEnougMoneyToSpin(): boolean {
		return this.logic.isEnoughMoneyForSpin();
	}

	public restore(): void {
		super.restore();

		this.canSpin = false;
		this.view.disableSpinButton();

		if (this.logic.isFreeSpinsStarted) {
			if (this.logic.freeSpinsLeft === this.logic.freespinsTotalAwarded) {
				this.gotoState(SpinButtonModule.FREE_SPINS_INTRO_STATE);
			} else {
				this.gotoState(SpinButtonModule.FREE_SPINS_STATE);
			}
		}
	}

	protected setupViews(): void {
		if (this.system.shouldUseMultipleViews()) {
			this.setView(Views.SPIN_BUTTON_VIEW_MOBILE);
		} else {
			this.setView(Views.SPIN_BUTTON_VIEW);
		}
	}

	protected setupEvents(): void {
		super.setupEvents();

		this.onCurrentStateHandle(ReelsEvents.SPIN_ENDED);
		this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onReelsSpinningStarted);

		this.onCurrentStateHandle(FreeSpinsEvents.FREE_SPINS_OUTRO_FINISHED);
		this.onCurrentStateHandle(FreeSpinsEvents.FREE_SPINS_INTRO_FINISHED);
		this.onCurrentStateHandle(FreeSpinsEvents.FREE_SPIN_STARTED);
		this.on(FreeSpinsEvents.FREE_SPIN_STARTED, this.onFreeSpinFlowStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_CONTINUE_CLICKED, this.onFreeSpinsIntroContinueClicked);
		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_FINISHED, this.onFreeSpinsIntroFinished);
		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_STARTED, this.onFreeSpinsIntroStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_FINISHED, this.onFreeSpinsOutroFinished);

		this.onCurrentStateHandle(FlowModuleEvents.CAN_SPIN);
		this.on(FlowModuleEvents.FREESPINS_WIN, this.onFreeSpinsWin);
		this.on(FlowModuleEvents.RESPIN_WON, this.onRespinWon);
		this.on(FlowModuleEvents.BONUS_GAME_START, this.onBonusGameStarted);
		this.on(FlowModuleEvents.BONUS_GAME_RESTORED, this.onBonusGameRestored);

		this.on(BonusGameEvents.BONUS_OUTRO_FINISHED, this.onBonusOutroFinished);

		this.on(LogicEvents.SPIN_RESPONSE, this.onSpinResponse);

		this.on(SwipeInteractionEvents.START_SPIN, this.onSwipeSpinStarted);

		this.on(SettingsEvents.TOGGLE_SPACEBAR_ON, this.onSpacebarOn);
		this.on(SettingsEvents.TOGGLE_SPACEBAR_OFF, this.onSpacebarOff);

		this.on(StartScreenEvents.INTRO_STARTED, this.onIntroStarted);
		this.on(StartScreenEvents.EXIT_FINISHED, this.onIntroFinished);

		this.on(BetlevelMenuEvents.BET_LEVEL_MENU_CLOSED, this.onBetLevelMenuClosed);
		this.on(BetlevelMenuEvents.BET_LEVEL_MENU_OPENED, this.onBetLevelMenuOpened);

		this.on(AutoplayMenuEvents.ACCEPTED, this.onStartAutoplay);
		this.on(AutoplayMenuEvents.AUTO_PLAY_MENU_CLOSED, this.onAutoPlayMenuClosed);
		this.on(AutoplayMenuEvents.AUTO_PLAY_MENU_OPENED, this.onAutoPlayMenuOpened);
		this.on(AutoplayHandlerEvents.START_AUTOPLAY_SPIN, this.onAutoplaySpinStarted);

		this.on(InfoPopupEvents.INFO_POPUP_CLOSED, this.onInfoPopupClosed);
		this.on(InfoPopupEvents.INFO_POPUP_OPENED, this.onInfoPopupOpened);

		this.on(StopAutoplayButtonEvents.STOP_AUTOPLAY_BUTTON_CLICKED, this.onAutoplayStopClicked);

		this.on(SpinButtonEvents.DISABLE_SPIN_BY_SPIN, this.onDisableSpinBySpin);
		this.on(SpinButtonEvents.ENABLE_SPIN_BY_SPIN, this.onEnableSpinBySpin);

		this.onView(SpinButtonViewEvents.SPIN_BUTTON_CLICKED, this.onSpinButtonClicked);
		this.onViewEmitEvent(SpinButtonView.SPIN_BUTTON_BECOMING_VISIBLE_AND_ENABLED, SpinButtonEvents.SPIN_BUTTON_BECOMING_VISIBLE_AND_ENABLED);

		this.onCurrentStateHandle(FlowModuleEvents.CAN_CONTINUE_SPIN);
		this.onCurrentStateHandle(SpinButtonEvents.REGULATION_SPIN_DELAY_PASSED);

		this.logic.spacebarSpinActive ? this.onSpacebarOn() : this.onSpacebarOff();
	}

	protected onDisableSpinBySpin(): void {
		this.view.enableSpinButton(false);
		this._canSpinBySpinButton = false;
	}

	protected onEnableSpinBySpin(): void {
		this._canSpinBySpinButton = true;
	}

	protected onFreeSpinsWin(): void {
		this._isInFreeSpins = true;
	}

	protected onRespinWon(): void {
		this.view.disableSpinButton();
	}

	protected onFreeSpinsIntroFinished(): void {
		this.gotoState(SpinButtonModule.FREE_SPINS_STATE);
	}

	protected onFreeSpinsIntroStarted(): void {
		this.view.hideSpinButton();
	}

	protected onFreeSpinsOutroFinished(): void {
		this.view.showSpinButton();
		this.view.enableSpinButton();
		this.canSpin = true;
		this._isInFreeSpins = false;
	}

	protected onInfoPopupOpened(): void {
		this._infoPopupOpen = true;
		this._isKeyboardInputEnabled = false;
	}

	protected onInfoPopupClosed(eventID: string, infoType: InfoPopupTypes): void {
		this._infoPopupOpen = false;
		this.tryEnableKeyboardInput();
	}

	protected tryEnableKeyboardInput(): void {
		if (!this._infoPopupOpen && !this._autoplayMenuOpen) {
			this._isKeyboardInputEnabled = true;
		}
	}

	protected addKeyboardInput(): void {
		this.removeKeyboardInput();
		this._inputFunction = this.keyboardInput.bind(this);
		document.addEventListener('keydown', this._inputFunction);
	}

	protected removeKeyboardInput(): void {
		document.removeEventListener('keydown', this._inputFunction);
	}

	protected keyboardInput(event: KeyboardEvent): void {
		if (!this._isKeyboardInputEnabled) {
			return;
		}

		if (event.code === 'Space' || event.keyCode === 32) {
			if (this.validateSpaceSpinPress()) {
				this.eventManager.emit(SpinButtonEvents.SPACE_BUTTON_CLICKED);
				this.currentState.handle(SpinButtonEvents.SPACE_BUTTON_CLICKED);
			}
		}
	}

	protected onBetLevelMenuOpened(): void {
		this._isKeyboardInputEnabled = false;
	}

	protected onBetLevelMenuClosed(): void {
		this.tryEnableKeyboardInput();
	}

	protected onAutoPlayMenuClosed(): void {
		this._autoplayMenuOpen = false;
		this.tryEnableKeyboardInput();
	}

	protected onAutoPlayMenuOpened(): void {
		this._autoplayMenuOpen = true;
		this._isKeyboardInputEnabled = false;
	}

	protected onIntroStarted(): void {
		this.canSpin = false;
	}

	protected onIntroFinished(): void {
		if (!this.device.isMobile) {
			this.addKeyboardInput();
		}
		this.canSpin = true;
		this.onCurrentStateHandle(StartScreenEvents.EXIT_FINISHED);
	}

	protected onSpacebarOn(): void {
		this._spaceRunsSpin = true;
	}

	protected onSpacebarOff(): void {
		this._spaceRunsSpin = false;
	}

	protected setupStates(): void {
		this.addState(new SpinButtonBasicState(this, SpinButtonModule.BASIC_STATE));
		this.addState(new SpinButtonFreeSpinsState(this, SpinButtonModule.FREE_SPINS_STATE));
		this.addState(new SpinButtonWhileSpinningState(this, SpinButtonModule.REELS_SPINNING_STATE));
		this.addState(new SpinButtonFreeSpinsIntoState(this, SpinButtonModule.FREE_SPINS_INTRO_STATE));
	}

	protected onAutoplayStopClicked(): void {
		this.eventManager.emit(SpinButtonEvents.STOP_AUTOPLAY_BUTTON_PRESSED);
		if (this.logic.freeSpinsLeft <= 0) {
			this.view.uninitAutoplay();
		}

		this._isInAutoplay = false;
	}

	protected onStartAutoplay(): void {
		this.view.initAutoplay();
		this._isInAutoplay = true;
	}

	protected onBonusGameStarted(): void {
		this.view.disableSpinButton();
		this.view.hide();
	}

	protected onBonusGameRestored(): void {
		this.view.hide();
	}

	protected onBonusOutroFinished(): void {
		this.view.show();
	}

	protected enterFirstState(): void {
		this.changeStateTo(SpinButtonModule.BASIC_STATE);
	}

	protected onSpinButtonClicked(): void {
		this.eventManager.emit(SpinButtonEvents.SPIN_BUTTON_PHYSICALLY_CLICKED);
		this.handleSpinButtonClick();
	}

	protected handleSpinButtonClick(): void {
		if (!this._canSpinBySpinButton) {
			this.eventManager.emit(SpinButtonEvents.SKIP_BY_SPIN_BUTTON_PRESSED);
			this.view.disableSpinButton();
		} else {
			this.currentState.handle(SpinButtonViewEvents.SPIN_BUTTON_CLICKED);
		}
	}

	protected onAutoplaySpinStarted(): void {
		this.eventManager.emit(SpinButtonEvents.START_AUTOPLAY_BUTTON_PRESSED);
		this.currentState.handle(SpinButtonEvents.START_AUTOPLAY_BUTTON_PRESSED);
	}

	protected onSwipeSpinStarted(): void {
		if (!this._isInAutoplay && this.canSpin) {
			this.handleSpinButtonClick();
		}
	}

	protected onFreeSpinFlowStarted(): void {
		if (this.canSpin && this.logic.isFreeSpinsStarted) {
			this.handleSpinButtonClick();
		}
	}

	protected onFreeSpinsIntroContinueClicked(): void {
		this.view.hide();
	}

	protected onReelsSpinningStarted(): void {
		this._hasSpinResponse = false;
		this.currentState.handle(ReelsEvents.REELS_SPINNING_STARTED);
	}

	protected onSpinResponse(): void {
		this._hasSpinResponse = true;
		this.currentState.handle(LogicEvents.SPIN_RESPONSE);
	}

	public set canSpin(v: boolean) {
		this._canSpin = v;
	}

	public get canSpin(): boolean {
		return this._canSpin;
	}

	public get hasQuickstop(): boolean {
		return this._hasQuickstop;
	}

	public get hasSpinResponse(): boolean {
		return this._hasSpinResponse;
	}

	public set regulationCanSpin(value: boolean) {
		this._regulationCanSpin = value;
	}

	public get regulationCanSpin(): boolean {
		return this._regulationCanSpin;
	}

	public get isInAutoplay(): boolean {
		return this._isInAutoplay;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/keypad/spinbutton/SpinButtonModule.ts